import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';

// Usar o mesmo domínio que a página está sendo servida (funciona em produção e preview)
const API = typeof window !== 'undefined' ? `${window.location.origin}/api` : '/api';

/**
 * Hook para gerenciar Push Notifications
 * Solicita permissão, registra subscription e gerencia estado
 */
export const usePushNotifications = () => {
  const [permission, setPermission] = useState('default');
  const [isSupported, setIsSupported] = useState(false);
  const [isSubscribed, setIsSubscribed] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Verificar suporte a push notifications
  useEffect(() => {
    const supported = 'serviceWorker' in navigator && 'PushManager' in window;
    setIsSupported(supported);
    
    if (supported) {
      setPermission(Notification.permission);
      checkSubscription();
    }
  }, []);

  // Verificar se já está inscrito
  const checkSubscription = async () => {
    try {
      const registration = await navigator.serviceWorker.ready;
      const subscription = await registration.pushManager.getSubscription();
      setIsSubscribed(!!subscription);
    } catch (e) {
      console.error('Error checking subscription:', e);
    }
  };

  // Converter base64 para Uint8Array (para VAPID key)
  const urlBase64ToUint8Array = (base64String) => {
    const padding = '='.repeat((4 - base64String.length % 4) % 4);
    const base64 = (base64String + padding)
      .replace(/-/g, '+')
      .replace(/_/g, '/');
    
    const rawData = window.atob(base64);
    const outputArray = new Uint8Array(rawData.length);
    
    for (let i = 0; i < rawData.length; ++i) {
      outputArray[i] = rawData.charCodeAt(i);
    }
    return outputArray;
  };

  // Solicitar permissão e inscrever
  const subscribe = useCallback(async () => {
    if (!isSupported) {
      setError('Push notifications não suportadas neste navegador');
      return false;
    }

    setLoading(true);
    setError(null);

    try {
      // Solicitar permissão
      const perm = await Notification.requestPermission();
      setPermission(perm);

      if (perm !== 'granted') {
        setError('Permissão negada para notificações');
        setLoading(false);
        return false;
      }

      // Obter chave pública VAPID do backend
      const keyResponse = await axios.get(`${API}/push/vapid-public-key`);
      const vapidPublicKey = keyResponse.data.publicKey;

      if (!vapidPublicKey) {
        throw new Error('VAPID public key not available');
      }

      // Registrar subscription
      const registration = await navigator.serviceWorker.ready;
      
      const subscription = await registration.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(vapidPublicKey)
      });

      // Enviar subscription para o backend
      const token = localStorage.getItem('token');
      await axios.post(
        `${API}/push/subscribe`,
        { subscription: subscription.toJSON() },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      setIsSubscribed(true);
      setLoading(false);
      return true;
    } catch (e) {
      console.error('Push subscription error:', e);
      setError(e.message || 'Erro ao ativar notificações');
      setLoading(false);
      return false;
    }
  }, [isSupported]);

  // Cancelar inscrição
  const unsubscribe = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const registration = await navigator.serviceWorker.ready;
      const subscription = await registration.pushManager.getSubscription();
      
      if (subscription) {
        await subscription.unsubscribe();
      }

      // Notificar backend
      const token = localStorage.getItem('token');
      if (token) {
        await axios.delete(`${API}/push/unsubscribe`, {
          headers: { Authorization: `Bearer ${token}` }
        });
      }

      setIsSubscribed(false);
      setLoading(false);
      return true;
    } catch (e) {
      console.error('Push unsubscribe error:', e);
      setError(e.message || 'Erro ao desativar notificações');
      setLoading(false);
      return false;
    }
  }, []);

  return {
    permission,
    isSupported,
    isSubscribed,
    loading,
    error,
    subscribe,
    unsubscribe
  };
};

export default usePushNotifications;
